import { Flex, TabNav, Text } from '@radix-ui/themes';
import React, { useEffect, useState, useCallback } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../config/axiosConfig';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import debounce from 'lodash/debounce';
import CreateUser from '../components/ui-components/CreateUser';
import SearchFilter from '../components/ui-components/SearchFilter';
import PaginationComponent from '../components/ui-components/PaginationComponent';

const UsersPage = () => {
  const navigate = useNavigate();
  const { user_type } = useParams();
  const roleId = localStorage.getItem('roleId');
  const selectedUserType = user_type || 'department';

  // State Management for data, pagination, and filtering
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const pageSize = 10;

  const userSubTab = [
    { tab: 'Department', key: 'department', roleId: 3 },
    { tab: 'Security', key: 'security', roleId: 5 },
    { tab: 'Housekeeping', key: 'housekeeping', roleId: 6 },
    { tab: 'F&B Users', key: 'f&b', roleId: 7 },
  ];

  useEffect(() => {
    if (!user_type) {
      navigate('department', { replace: true });
    }
  }, [user_type, navigate]);

  const showToast = (message, type = 'success') => {
    toast(message, { type });
  };
  
  const fetchUsers = useCallback(async (page = 1, query = '', activeOnly = false) => {
    const currentTab = userSubTab.find(tab => tab.key === selectedUserType);
    if (!currentTab) return;

    try {
      const payload = {
        searchTerm: query.length >= 3 ? query : null,
        isActive: activeOnly ? true : null,
        pageNumber: page,
        pageSize: pageSize,
        roleId: currentTab.roleId,
      };
      const response = await axiosInstance.post('/user/get-users', payload);
      
      if (response.data?.result?.userGetList) {
        setUsers(response.data.result.userGetList);
        setTotalCount(response.data.result.totalCount || 0);
      } else {
        setUsers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching users:', error.response ? error.response.data : error.message);
      setUsers([]);
      setTotalCount(0);
      showToast('Failed to fetch users', 'error');
    }
  }, [selectedUserType]);

  const debouncedFetch = useCallback(debounce(fetchUsers, 300), [fetchUsers]);

  useEffect(() => {
    // Only trigger fetch if search is empty or has enough characters
    if (searchQuery.length === 0 || searchQuery.length >= 3) {
        debouncedFetch(currentPage, searchQuery, showActiveOnly);
    }
    return () => debouncedFetch.cancel();
  }, [currentPage, searchQuery, showActiveOnly, debouncedFetch]);

  // This handler is called by the SearchFilter's onSearchChange prop
  const handleSearchChange = (value) => {
    // The main job of this parent handler is to reset the page number
    setCurrentPage(1);
  };

  // This handler is called by the SearchFilter's onActiveToggle prop
  const handleActiveToggle = (checked) => {
    // The main job of this parent handler is to reset the page number
    setCurrentPage(1);
  };
  
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <Flex direction={'column'} gap={'3'}>
        <Flex justify={'between'} align="center" gap="3" wrap="wrap">
          {/* CORRECTED: Pass the required state setter functions */}
          <SearchFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery} // <-- Pass the setter function
            showActiveOnly={showActiveOnly}
            setShowActiveOnly={setShowActiveOnly} // <-- Pass the setter function
            placeholder="Search by Dept, Email, Username..."
            onSearchChange={handleSearchChange}
            onActiveToggle={handleActiveToggle}
          />
          <CreateUser roleId={roleId} onUserCreated={() => fetchUsers(1, searchQuery, showActiveOnly)} />
        </Flex>

        <TabNav.Root>
          {userSubTab.map(({ tab, key }) => (
            <TabNav.Link
              key={key}
              active={selectedUserType === key}
              onClick={() => {
                setCurrentPage(1); 
                setSearchQuery('');
                setShowActiveOnly(false);
                navigate(key);
              }}
            >
              {tab}
            </TabNav.Link>
          ))}
        </TabNav.Root>

        <Outlet context={{ users, fetchUsers, showToast, currentPage, pageSize }} />
        
        {totalCount > 0 && (
          <Flex direction="column" align="center" gap="2" mt="4">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(newPage) => setCurrentPage(newPage)}
            />
            <Text size="2">
              Showing {(currentPage - 1) * pageSize + 1} -{' '}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
            </Text>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default UsersPage;
